import { Timestamp } from 'firebase/firestore';

export type BusinessStatus = 'active' | 'suspended';

// Forward declaration for BusinessLocalization (defined later in file)
export interface Business {
  id: string;
  agencyId?: string;
  name: string;
  phoneNumber: string;
  timezone: string;
  businessHours: BusinessHours;
  services: Service[];
  faqs: FAQ[];
  greeting: string;
  voiceId: string;
  transferNumber?: string;
  calendarId?: string;
  settings: BusinessSettings;
  localization?: { language: 'en' | 'es' };
  // AI Configuration - the system prompt that defines AI behavior
  systemPrompt?: string;
  smsEnabled?: boolean;
  webhookEvents?: string[];
  googleCalendar?: {
    connected: boolean;
    connectedAt?: Timestamp;
  };
  stats?: {
    totalCallsThisMonth?: number;
    totalLeads?: number;
    totalAppointments?: number;
  };
  // Contact information for the business owner/manager
  contactFirstName?: string;
  contactLastName?: string;
  // Business status - active businesses receive calls, suspended do not
  status?: BusinessStatus;
  suspendedAt?: Timestamp;
  suspendedReason?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface BusinessHours {
  [day: string]: {
    open: string;
    close: string;
    closed?: boolean;
  };
}

export interface Service {
  id: string;
  name: string;
  description: string;
  duration: number;
  price?: number;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
}

export interface BusinessSettings {
  maxCallDuration: number;
  // Retell AI agent configuration
  retellAgentId?: string;
  retellLlmId?: string;
  retellPhoneNumberId?: string;
}

export interface BusinessMember {
  userId: string;
  businessId: string;
  role: 'owner' | 'admin' | 'member';
  email: string;
  name: string;
  createdAt: Timestamp;
}

export interface CallLog {
  id: string;
  callSid: string;
  callerNumber: string;
  calledNumber: string;
  status: string;
  duration: number;
  conversationHistory: ConversationTurn[];
  extractedData: ExtractedData;
  startedAt: Timestamp;
  endedAt?: Timestamp;
  recordingUrl?: string;
  createdAt: Timestamp;
}

export interface ConversationTurn {
  role: 'system' | 'assistant' | 'user';
  content: string;
  timestamp: Timestamp;
  intent?: string;
  confidence?: number;
}

export interface ExtractedData {
  callerName?: string;
  callerPhone?: string;
  callerEmail?: string;
  reason?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  serviceRequested?: string;
  notes?: string;
  [key: string]: string | undefined;
}

export interface Lead {
  id: string;
  businessId: string;
  callSessionId: string;
  callerName?: string;
  callerPhone: string;
  callerEmail?: string;
  reason?: string;
  notes?: string;
  status: LeadStatus;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';

export interface Appointment {
  id: string;
  businessId: string;
  callSessionId?: string;
  calendarEventId?: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  serviceId?: string;
  serviceName: string;
  scheduledAt: Timestamp;
  duration: number;
  status: AppointmentStatus;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export type AppointmentStatus = 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no-show';

// Input type for creating appointments (scheduledAt as string from form)
export interface AppointmentInput {
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  serviceId?: string;
  serviceName: string;
  scheduledAt: string;
  duration: number;
  notes?: string;
}

export interface Voice {
  id: string;
  name: string;
}

// Retell AI types
export interface RetellVoice {
  voice_id: string;
  voice_name: string;
  provider: string;
  accent?: string;
  gender?: string;
  age?: string;
  preview_audio_url?: string;
}

export interface RetellAgentStatus {
  hasAgent: boolean;
  agentId?: string;
  llmId?: string;
  agentName?: string;
  voiceId?: string;
  hasPhoneNumber: boolean;
  phoneNumber?: string;
  phoneNumberId?: string;
}

export interface RetellPhoneNumber {
  phone_number: string;
  phone_number_id: string;
  nickname?: string;
  inbound_agent_id?: string;
}

export interface CalendarSlot {
  start: Date;
  end: Date;
  available: boolean;
}

// Localization types
export type SupportedCountry = 'US' | 'UK' | 'MX' | 'CA';
export type SupportedCurrency = 'usd' | 'gbp' | 'mxn' | 'cad';
export type SupportedLanguage = 'en' | 'es';

export interface AgencyLocalization {
  country: SupportedCountry;
  currency: SupportedCurrency;
  language: SupportedLanguage;
}

export interface BusinessLocalization {
  language: SupportedLanguage;
}

// Agency types
export interface Agency {
  id: string;
  name: string;
  slug: string;
  ownerId: string;
  ownerEmail?: string;
  settings: AgencySettings;
  localization?: AgencyLocalization;
  billingPlan: 'starter' | 'professional' | 'enterprise';
  billing?: AgencyBilling;
  stripeConnect?: StripeConnectStatus;
  maxBusinesses: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface AgencyBilling {
  stripeCustomerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete';
  currentPeriodEnd?: Timestamp;
  plan: 'starter' | 'professional' | 'enterprise';
}

export interface StripeConnectStatus {
  accountId: string;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  priceId: string;
  priceCents: number;
  maxBusinesses: number;
  features: string[];
}

export interface SubscriptionStatus {
  plan: 'starter' | 'professional' | 'enterprise';
  status: string;
  currentPeriodEnd: Date | null;
  features: string[];
  priceCents: number;
}

export interface AgencySettings {
  defaultTimezone: string;
  defaultVoiceId: string;
  webhookUrl?: string;
  webhookSecret?: string;
}

export interface AgencyMember {
  id: string;
  userId: string;
  agencyId: string;
  role: 'owner' | 'admin' | 'viewer';
  email: string;
  name: string;
  createdAt: Timestamp;
}

// Utility function
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}
